<template>
  <div class="kf-page-header">
    <div v-if="title || backTo" class="kf-page-header__left">
      <v-btn v-if="backTo" icon small class="mr-1" @click="goBack">
        <LucideIcon name="arrow-left" :size="20" />
      </v-btn>
      <div>
        <div v-if="title" class="kf-page-header__title">{{ title }}</div>
        <div v-if="subtitle" class="kf-page-header__subtitle">{{ subtitle }}</div>
      </div>
    </div>
    <div class="kf-page-header__right">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { hasAppHistory } from "@/app-js/router";

export default {
  name: "KfPageHeader",
  props: {
    /**
     * Only the detail screens name themselves here, and what they show is the
     * record -- the customer, the product -- not the screen. The list screens
     * leave it empty, because the navbar already says which screen you are on
     * and repeating it stole the top of the page.
     *
     * Products and the Dashboard no longer render this component at all: their
     * one button now rides on the right of their ProductSwitch row, which costs
     * no vertical space. Reports and Team still use it title-less, so the
     * title-less path is live and the slot must right-align on its own -- see
     * the margin-left:auto note on __right below.
     */
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    /**
     * Where to go when there is no history to pop -- a deep link or a hard
     * refresh. Also what makes the back arrow render at all. Think of it as
     * the page's declared parent, not as the only way out: a lead can be
     * opened from Leads, Applications, the Dashboard or an offer, and back
     * should return to whichever of those the user actually came from.
     */
    backTo: { type: [Object, String], default: null },
  },
  methods: {
    goBack() {
      // Popping also restores the previous screen's filters, which live in its
      // query string; pushing `backTo` would silently drop them.
      if (hasAppHistory()) this.$router.back();
      else this.$router.push(this.backTo).catch(() => {});
    },
  },
};
</script>

<style scoped>
.kf-page-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.kf-page-header__left {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}
.kf-page-header__title {
  font-size: 20px;
  font-weight: 700;
  color: var(--kf-brand);
  line-height: 1.3;
}
/* The line under a detail screen's title -- the lead's offers, city and signup
   date. It sits on the grey canvas rather than on a white card, which is the worst
   background in the app to be small and grey on, so it is the one piece of
   secondary text that gets pure black and the largest of the secondary sizes.
   Note this makes it DARKER than the navy title above it: the hierarchy is
   carried by size and weight (20px/700 vs 15.5px/600), not by contrast. */
.kf-page-header__subtitle {
  font-size: var(--kf-fs-sub);
  font-weight: 600;
  color: #000000;
  margin-top: 3px;
  line-height: 1.45;
  max-width: 760px;
}
/*
  margin-left:auto rather than justify-content:space-between on the parent.
  space-between only pushes this over when __left is actually rendered, and
  __left is v-if'd away on a title-less page -- so the action row, which this
  component's whole purpose is to put on the RIGHT, sat flush left on every
  screen that passed no title (Products, Dashboard, Reports, Team). auto margin
  right-aligns it whether __left exists or not, and behaves identically to
  space-between when it does.
*/
.kf-page-header__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
